import { callActor } from '../call';
import {
  CallState,
  type CallStatePhoneCallLeavingSubState,
} from '../call/call.types';

type CallActorSnapshot = ReturnType<typeof callActor.getSnapshot>;

export type PhoneCallLeavingReason =
  CallStatePhoneCallLeavingSubState[typeof CallState.PhoneCallLeaving];

const PHONE_CALL_LEAVING_REASONS = new Set<PhoneCallLeavingReason>([
  'providerCanceled',
  'providerCanceledRinging',
  'patientDeclined',
  'patientUnanswered',
  'patientCanceled',
]);

export const getPhoneCallLeavingReason = (
  callMachineState: CallActorSnapshot,
): PhoneCallLeavingReason | undefined => {
  if (typeof callMachineState.value === 'string') {
    return undefined;
  }

  const leavingReason = callMachineState.value[CallState.PhoneCallLeaving];

  if (
    typeof leavingReason === 'string' &&
    PHONE_CALL_LEAVING_REASONS.has(leavingReason as PhoneCallLeavingReason)
  ) {
    return leavingReason as PhoneCallLeavingReason;
  }

  return undefined;
};

export const isPhonePostCallSurveyEligible = (
  leavingReason: PhoneCallLeavingReason | undefined,
): boolean =>
  leavingReason === 'providerCanceled' || leavingReason === 'patientCanceled';

export const isPhoneCallLeavingAfterError = (
  callMachineState: CallActorSnapshot,
): boolean =>
  typeof callMachineState.value === 'string' &&
  callMachineState.value === CallState.PhoneCallLeavingAfterError;

export const isPhonePostCallTryAgainEligible = (
  leavingReason: PhoneCallLeavingReason | undefined,
  isLeavingAfterError = false,
): boolean =>
  isLeavingAfterError ||
  leavingReason === 'patientUnanswered' ||
  leavingReason === 'patientDeclined' ||
  leavingReason === 'providerCanceledRinging';

export type PhonePostCallNotConnectedVariant =
  | 'unanswered'
  | 'rejected'
  | 'failed';

export const getPhonePostCallNotConnectedVariant = (
  leavingReason: PhoneCallLeavingReason | undefined,
  isLeavingAfterError = false,
): PhonePostCallNotConnectedVariant | undefined => {
  if (isLeavingAfterError) {
    return 'failed';
  }

  if (leavingReason === 'patientUnanswered') {
    return 'unanswered';
  }

  if (leavingReason === 'patientDeclined') {
    return 'rejected';
  }

  return undefined;
};
